import { compileProjectDocument } from "./project-compiler.js";
import { assertValidProjectDocument, deriveProjectTimeline, type ProjectDocument } from "./project-schema.js";

export type PreviewMode = "scene" | "transition" | "range" | "full";

export type PreviewWindow = {
  start: number;
  end: number;
  duration: number;
  label: string;
};

export type PreviewSelection = {
  mode: PreviewMode;
  sceneId?: string;
  transitionId?: string;
  range?: { start: number; end: number };
};

function boundedWindow(start: number, end: number, total: number, fps: number, label: string): PreviewWindow {
  const frame = 1 / fps;
  const safeStart = Math.max(0, Math.min(start, Math.max(0, total - frame)));
  const safeEnd = Math.max(safeStart + frame, Math.min(end, total));
  return { start: safeStart, end: safeEnd, duration: safeEnd - safeStart, label };
}

export function resolvePreviewWindow(project: ProjectDocument, selection: PreviewSelection): PreviewWindow {
  assertValidProjectDocument(project);
  const timeline = deriveProjectTimeline(project);
  if (selection.mode === "full") return boundedWindow(0, timeline.duration, timeline.duration, project.format.fps, "Complete composition");

  if (selection.mode === "scene") {
    const timing = timeline.scenes.find((scene) => scene.id === selection.sceneId);
    const scene = project.scenes.find((item) => item.id === selection.sceneId);
    if (!timing || !scene) throw new Error(`Preview scene "${selection.sceneId ?? ""}" does not exist.`);
    return boundedWindow(timing.start, timing.end, timeline.duration, project.format.fps, `Scene · ${scene.family}`);
  }

  if (selection.mode === "transition") {
    const transition = project.transitions.find((item) => item.id === selection.transitionId);
    if (!transition) throw new Error(`Preview transition "${selection.transitionId ?? ""}" does not exist.`);
    const incoming = timeline.scenes.find((scene) => scene.id === transition.toSceneId)!;
    const handle = transition.type === "cut" ? 0.4 : 0.25;
    return boundedWindow(incoming.start - handle, incoming.start + transition.duration + handle, timeline.duration, project.format.fps, `Transition · ${transition.type}`);
  }

  const requested = selection.range ?? { start: 0, end: timeline.duration };
  return boundedWindow(requested.start, requested.end, timeline.duration, project.format.fps, "Selected range");
}

const PREVIEW_BRIDGE = String.raw`
<style data-hf-preview-shell>
  html,body{width:100vw!important;height:100vh!important;overflow:hidden!important;background:#05070a!important}
  body{position:relative;display:block}
  #root{position:absolute!important;transform-origin:0 0;will-change:transform}
  html[data-hf-preview-quality="mobile"] #root{will-change:auto}
</style>
<script data-hf-preview-bridge>
(()=>{
  const source="hf-deterministic-preview";
  const root=document.getElementById("root");
  const compositionId=root?.dataset.compositionId;
  const timeline=window.__timelines?.[compositionId];
  const width=Number(root?.dataset.width||1920);
  const height=Number(root?.dataset.height||1080);
  const duration=Number(root?.dataset.duration||0);
  const fps=Number(root?.dataset.fps||30);
  const clips=[...document.querySelectorAll(".clip[data-start][data-duration]")];
  const carouselImages=[...document.querySelectorAll(".fc-card .hfp-card-image-loader")];
  let quality="full";
  try{quality=parent.matchMedia("(max-width: 720px)").matches?"mobile":"full"}catch{}
  let preparedQuality="";
  let qualityRequest=0;
  let playbackFrame=0;
  let playbackStartedAt=0;
  let playbackStartedFrom=0;
  let lastProgressAt=-Infinity;
  let lastRenderedFrame=-1;
  const fit=()=>{
    if(!root)return;
    const scale=Math.min(innerWidth/width,innerHeight/height);
    const x=(innerWidth-width*scale)/2;
    const y=(innerHeight-height*scale)/2;
    root.style.transform="translate("+x+"px,"+y+"px) scale("+scale+")";
  };
  const applyQuality=()=>{
    document.documentElement.dataset.hfPreviewQuality=quality;
  };
  const renderAt=(requested)=>{
    const time=Math.max(0,Math.min(Number(requested)||0,duration));
    timeline.pause().seek(time,false);
    clips.forEach((clip)=>{
      const start=Number(clip.dataset.start||0);
      const end=start+Number(clip.dataset.duration||0);
      clip.style.visibility=time>=start&&time<end?"visible":"hidden";
    });
    return time;
  };
  const prepareMobileImages=async()=>{
    if(quality!=="mobile")return;
    const seen=new Set();
    const images=carouselImages.filter((image)=>{
      const source=image.currentSrc||image.src||"";
      if(!source||seen.has(source))return false;
      seen.add(source);
      return true;
    });
    await Promise.allSettled(images.map((image)=>typeof image.decode==="function"?image.decode():Promise.resolve()));
    await new Promise((resolve)=>requestAnimationFrame(()=>requestAnimationFrame(resolve)));
  };
  const prepareQuality=async(requested)=>{
    const request=++qualityRequest;
    quality=requested==="mobile"?"mobile":"full";
    preparedQuality="";
    lastRenderedFrame=-1;
    applyQuality();
    await prepareMobileImages();
    if(request!==qualityRequest)return;
    preparedQuality=quality;
    parent.postMessage({source,type:"quality",quality},"*");
  };
  const seek=(requested)=>{
    const time=renderAt(requested);
    parent.postMessage({source,type:"frame",time},"*");
  };
  const pause=(announce=true)=>{
    if(playbackFrame)cancelAnimationFrame(playbackFrame);
    playbackFrame=0;
    const time=Number(timeline.time())||0;
    if(announce)parent.postMessage({source,type:"progress",time,playing:false},"*");
  };
  const play=(requested)=>{
    if(preparedQuality!==quality)return;
    pause(false);
    playbackStartedFrom=Math.max(0,Math.min(Number(requested)||0,Math.max(0,duration-1/60)));
    playbackStartedAt=performance.now();
    lastProgressAt=-Infinity;
    lastRenderedFrame=-1;
    const tick=(now)=>{
      const time=playbackStartedFrom+(now-playbackStartedAt)/1000;
      if(time>=duration){
        renderAt(Math.max(0,duration-1/fps));
        playbackFrame=0;
        parent.postMessage({source,type:"progress",time:duration,playing:false},"*");
        return;
      }
      const frameIndex=Math.floor(time*fps);
      const shouldRender=quality!=="mobile"||frameIndex!==lastRenderedFrame;
      const current=shouldRender?renderAt(quality==="mobile"?frameIndex/fps:time):Number(timeline.time())||0;
      if(shouldRender)lastRenderedFrame=frameIndex;
      if(now-lastProgressAt>=250){
        lastProgressAt=now;
        parent.postMessage({source,type:"progress",time:current,playing:true},"*");
      }
      playbackFrame=requestAnimationFrame(tick);
    };
    playbackFrame=requestAnimationFrame(tick);
  };
  addEventListener("resize",fit);
  if(!timeline){
    parent.postMessage({source,type:"error",message:"Compiled timeline did not register."},"*");
    return;
  }
  addEventListener("message",(event)=>{
    if(event.data?.source!=="hf-editor-preview")return;
    if(event.data.type==="seek"){
      pause(false);
      seek(event.data.time);
    }
    if(event.data.type==="play")play(event.data.time);
    if(event.data.type==="pause")pause();
    if(event.data.type==="quality")void prepareQuality(event.data.quality);
  });
  applyQuality();
  fit();
  seek(0);
  parent.postMessage({source,type:"ready",compositionId,duration,width,height},"*");
})();
</script>`;

export function compileProjectPreviewDocument(project: ProjectDocument): string {
  const html = compileProjectDocument(project);
  return html.replace("</body>", `${PREVIEW_BRIDGE}\n</body>`);
}
